import { useEffect, useMemo, useRef, useState } from "react";
import rules from "../../data/cftcRules.json";
import {
  evaluateRules,
  type CftcRules,
  type EvaluationResult,
  type ReadinessState,
} from "./cftcRulesEngine.ts";
import { applyLiveDecayToReadiness, type PriceReferenceMode } from "./priceReferenceUtils.ts";

type UseCftcSignalsArgs = {
  context: Record<string, unknown> | null;
  assetClass: string;
  symbolKey: string;
  alertsEnabled: boolean;
  priceMode: PriceReferenceMode;
  liveDecay?: number;
};

const COOLDOWN_STORAGE_KEY = "cftcAlertCooldowns";

const loadCooldowns = () => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const stored = window.localStorage.getItem(COOLDOWN_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (err) {
    return {};
  }
};

const saveCooldowns = (cooldowns: Record<string, number>) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify(cooldowns));
  } catch (err) {
    // Ignore storage failures.
  }
};

export const useCftcSignals = ({
  context,
  assetClass,
  symbolKey,
  alertsEnabled,
  priceMode,
  liveDecay,
}: UseCftcSignalsArgs) => {
  const [cooldowns, setCooldowns] = useState<Record<string, number>>(loadCooldowns);
  const readinessStateRef = useRef<Record<string, ReadinessState>>({});
  const ruleset = rules as CftcRules;

  const evaluation: EvaluationResult = useMemo(() => {
    return evaluateRules(context, ruleset, assetClass, {
      now: Date.now(),
      cooldownByAlert: cooldowns,
      symbolKey,
      readinessState: readinessStateRef.current[symbolKey],
    });
  }, [context, assetClass, cooldowns, symbolKey, ruleset]);

  useEffect(() => {
    if (!alertsEnabled) {
      return;
    }
    const updates = evaluation.cooldownUpdates;
    const updateKeys = Object.keys(updates);
    if (!updateKeys.length) {
      return;
    }
    setCooldowns((prev) => {
      const next = { ...prev, ...updates };
      saveCooldowns(next);
      return next;
    });
  }, [alertsEnabled, evaluation.cooldownUpdates]);

  const alerts = alertsEnabled
    ? evaluation.alerts.map((alert) => ({ ...alert, mode: priceMode }))
    : [];

  const readiness = useMemo(() => {
    const baseReadiness =
      priceMode === "live" && typeof liveDecay === "number" && Number.isFinite(liveDecay)
        ? applyLiveDecayToReadiness(
            evaluation.readiness,
            liveDecay,
            ruleset.readinessScore.range,
            ruleset.readinessScore.labels
          )
        : evaluation.readiness;
    return { ...baseReadiness, mode: priceMode };
  }, [evaluation.readiness, liveDecay, priceMode, ruleset]);

  useEffect(() => {
    if (!symbolKey) {
      return;
    }
    readinessStateRef.current[symbolKey] = evaluation.readinessState;
  }, [evaluation.readinessState, symbolKey]);

  return {
    alerts,
    readiness,
    badge: evaluation.badge,
  };
};
